import { Portal } from '@ark-ui/react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from 'lib/utils'
import { isValidElement, ReactNode } from 'react'

type TooltipProps = TooltipPrimitive.TooltipProps & {
  tooltip: ReactNode
  usePortal?: boolean
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  disabled?: boolean
  className?: string
}

const TooltipProvider = TooltipPrimitive.Provider

function Tooltip({
  children,
  tooltip,
  usePortal,
  className,
  side = 'bottom',
  sideOffset = 4,
  disabled,
  ...props
}: TooltipProps) {
  const asChild = asChildSupported(children)

  if (disabled) {
    return <>{children}</>
  }

  return (
    <TooltipPrimitive.Root delayDuration={500} {...props}>
      <TooltipPrimitive.Trigger asChild={asChild}>{children}</TooltipPrimitive.Trigger>
      <Portal disabled={!usePortal}>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={sideOffset}
          className={cn(
            'bg-zinc-900 text-white animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-lg px-3 py-1.5 text-xs text-balance',
            className,
          )}
        >
          {tooltip}
          <TooltipPrimitive.Arrow className='bg-zinc-900 fill-zinc-900 z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]' />
        </TooltipPrimitive.Content>
      </Portal>
    </TooltipPrimitive.Root>
  )
}

function asChildSupported(children: ReactNode) {
  if (!children) return false

  if (!isValidElement(children)) return false

  const isDomElement = typeof children.type === 'string'

  const isForwardRef = children.type?.['$$typeof'] === Symbol.for('react.forward_ref')
  return isDomElement || isForwardRef
}

export { Tooltip, TooltipProvider }
